/**
 * Projecttijdlijn in de stijl van de tijdlijnweergave van MS Project (SPEC §4, projectplanning):
 * tijdschaal met maanden en weken, "Vandaag", een kader met project, werkpakketten, sprints en
 * feestdagen, en daaronder de mijlpalen (werkpakket gereed, livegang, oplevering) met labels.
 */
import { addDagen, verschilDagen } from '@ep/core';
import type { Data, ProjectRij } from '../lib/data';

const MAAND = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const DAG = ['zon', 'maa', 'din', 'woe', 'don', 'vri', 'zat'];
/** "don 1-10-26" */
export const kortDatum = (d: string) => {
  const x = new Date(`${d}T12:00:00Z`);
  return `${DAG[x.getUTCDay()]} ${x.getUTCDate()}-${x.getUTCMonth() + 1}-${String(x.getUTCFullYear()).slice(2)}`;
};
const KLEUREN = ['#d9d9d9', '#dbe8f7', '#fdf1c7', '#e1f2dc', '#e6e0f4', '#fde3cf', '#d7eef0', '#f3e1d3'];

interface Balk { id: string; naam: string; start: string; eind: string; kleur: string; sub?: string }
interface Mijlpaal { id: string; naam: string; datum: string; nadruk?: boolean }

export function ProjectTijdlijn({ d, p }: { d: Data; p: ProjectRij }) {
  const vd = d.vandaag;
  const wps = d.werkpakketten.filter((w) => w.projectId === p.id).sort((a, b) => a.volgorde - b.volgorde);
  const taken = d.taken.filter((t) => t.projectId === p.id && t.startdatum);
  const sprints = (d.sprints ?? []).filter((s) => wps.some((w) => w.id === s.werkpakketId));

  // werkpakketten: van de eerste taak (of het einde van het vorige werkpakket) tot de deadline (of de laatste taak)
  const balken: Balk[] = [];
  let vorigEind: string | null = null;
  wps.forEach((w, i) => {
    const eigen = taken.filter((t) => t.werkpakketId === w.id);
    const sp = sprints.filter((s) => s.werkpakketId === w.id);
    const starts = [...eigen.map((t) => t.startdatum!), ...sp.map((s) => s.start)].sort();
    const einden = [...eigen.map((t) => t.einddatum ?? t.startdatum!), ...sp.map((s) => s.eind)].sort();
    const start = starts[0] ?? (vorigEind ? addDagen(vorigEind, 1) : p.start ?? vd);
    const eind = w.deadline ?? einden.at(-1) ?? addDagen(start, 13);
    balken.push({ id: w.id, naam: w.naam, start: start <= eind ? start : eind, eind, kleur: KLEUREN[i % KLEUREN.length] });
    vorigEind = eind;
  });
  const pStart = p.start ?? balken[0]?.start ?? vd;
  const pEind = p.livegang ?? p.oplevering ?? balken.at(-1)?.eind ?? addDagen(pStart, 30);

  const mijlpalen: Mijlpaal[] = [
    ...wps.filter((w) => w.deadline).map((w) => ({ id: `wp-${w.id}`, naam: `${w.naam} gereed`, datum: w.deadline! })),
    ...(p.livegang ? [{ id: 'livegang', naam: 'Livegang', datum: p.livegang, nadruk: true }] : []),
    ...(p.oplevering ? [{ id: 'oplevering', naam: 'Oplevering', datum: p.oplevering, nadruk: true }] : []),
  ].sort((a, b) => a.datum.localeCompare(b.datum));
  const feest = d.feestdagen.filter((f) => f.datum >= addDagen(pStart, -14));

  // bereik van de schaal: alles wat getoond wordt, met een week marge
  const alle = [pStart, pEind, vd, ...balken.flatMap((b) => [b.start, b.eind]), ...mijlpalen.map((m) => m.datum)].sort();
  const van = addDagen(alle[0], -10);
  const tot = addDagen(alle.at(-1)!, 10);
  const totaal = Math.max(1, verschilDagen(van, tot));
  const L = 170, R = 170, B = 1600, breed = B - L - R;
  const X = (dt: string) => L + (verschilDagen(van, dt) / totaal) * breed;

  // weken (maandagen) en maanden
  const weken: string[] = [];
  for (let x = van; x <= tot; x = addDagen(x, 1)) if (new Date(`${x}T12:00:00Z`).getUTCDay() === 1) weken.push(x);
  const maanden: string[] = [];
  for (let x = van; x <= tot; x = addDagen(x, 1)) if (x.endsWith('-01')) maanden.push(x);
  const stap = weken.length > 40 ? 2 : 1;

  // kader
  const y0 = 92, rijH = 52;
  const rijFeest = y0 + 12, rijProj = rijFeest + 44, rijWp = rijProj + rijH + 4, rijSprint = rijWp + rijH + 4;
  const kaderEind = (sprints.length ? rijSprint + 34 : rijWp + rijH + 20) + 14;

  // mijlpaallabels: niet over elkaar heen (per niveau een eigen hoogte)
  const niveaus: [number, number][][] = [];
  const geplaatst = mijlpalen.map((m) => {
    const x = X(m.datum);
    const w = Math.max(m.naam.length * 10.5, 120);
    let n = 0;
    // een label past op een niveau als het niet overlapt met de labels die daar al staan
    while ((niveaus[n] ?? []).some((r) => Math.abs(r[0] - x) < (w + r[1]) / 2 + 16)) n++;
    (niveaus[n] ??= []).push([x, w]);
    return { ...m, x, n };
  });
  const H = kaderEind + 40 + (Math.max(0, ...geplaatst.map((g) => g.n)) + 1) * 72 + 10;
  const clip = (id: string) => `url(#clip-${id})`;
  const ink = 'var(--ink)', mute = 'var(--mute)';

  return (
    <svg viewBox={`0 0 ${B} ${H}`} style={{ width: '100%', minWidth: 900, display: 'block' }} role="img" aria-label={`Tijdlijn ${p.nummer} ${p.naam}`} fontFamily="inherit">
      <defs>
        {[...balken, { id: 'project' }].map((b) => (
          <clipPath key={b.id} id={`clip-${b.id}`}>
            <rect x={b.id === 'project' ? X(pStart) : X((b as Balk).start)} y={0} width={Math.max(4, b.id === 'project' ? X(addDagen(pEind, 1)) - X(pStart) : X(addDagen((b as Balk).eind, 1)) - X((b as Balk).start) - 2)} height={H} />
          </clipPath>
        ))}
      </defs>

      {/* vandaag */}
      {vd >= van && vd <= tot && (
        <g>
          <rect x={X(vd) - 52} y={8} width={104} height={28} fill="#3d7a35" />
          <text x={X(vd)} y={28} textAnchor="middle" fontSize={17} fontWeight={700} fill="#fff">Vandaag</text>
        </g>
      )}
      {/* maanden en weken */}
      {maanden.map((m) => (
        <text key={m} x={X(m) + 4} y={52} fontSize={15} fontWeight={700} fill="var(--accent)">{MAAND[Number(m.slice(5, 7)) - 1]} {m.slice(0, 4)}</text>
      ))}
      <line x1={L} x2={B - R} y1={y0 - 6} y2={y0 - 6} stroke={ink} strokeWidth={1} />
      {weken.map((w, i) => (
        <g key={w}>
          <line x1={X(w)} x2={X(w)} y1={y0 - 26} y2={y0 - 6} stroke={ink} strokeWidth={1} />
          {i % stap === 0 && <text x={X(w) + 5} y={y0 - 11} fontSize={16} fill={ink}>{Number(w.slice(8))}</text>}
        </g>
      ))}

      {/* kader met begin- en einddatum */}
      <rect x={L} y={y0} width={breed} height={kaderEind - y0} fill="none" stroke="#555" strokeWidth={3} />
      <text x={L - 12} y={y0 + 20} textAnchor="end" fontSize={16} fill={ink}>Begindatum</text>
      <text x={L - 12} y={y0 + 42} textAnchor="end" fontSize={16} fill={ink}>{kortDatum(pStart)}</text>
      <text x={B - R + 12} y={y0 + 20} fontSize={16} fill={ink}>Einddatum</text>
      <text x={B - R + 12} y={y0 + 42} fontSize={16} fill={ink}>{kortDatum(pEind)}</text>

      {/* feestdagen */}
      {feest.filter((f) => f.datum >= van && f.datum <= tot).map((f) => (
        <rect key={f.id} x={X(f.datum)} y={rijFeest} width={Math.max(8, X(addDagen(f.datum, 1)) - X(f.datum))} height={38} fill="#f5c6cc"><title>{f.naam} · {kortDatum(f.datum)}</title></rect>
      ))}

      {/* project */}
      <g>
        <rect x={X(pStart)} y={rijProj} width={Math.max(4, X(addDagen(pEind, 1)) - X(pStart))} height={rijH - 4} fill="#f1c6ec"><title>{p.nummer} – {p.naam}</title></rect>
        <text x={X(pStart) + 10} y={rijProj + 22} fontSize={16} fontWeight={700} fill="#333" clipPath={clip('project')}>{p.nummer} – {p.naam}{p.klant ? ` · ${p.klant}` : ''}</text>
      </g>

      {/* werkpakketten */}
      {balken.map((b) => (
        <g key={b.id}>
          <rect x={X(b.start)} y={rijWp} width={Math.max(6, X(addDagen(b.eind, 1)) - X(b.start) - 2)} height={rijH} fill={b.kleur}><title>{b.naam}: {kortDatum(b.start)} – {kortDatum(b.eind)}</title></rect>
          <text x={X(b.start) + 10} y={rijWp + 21} fontSize={16} fontWeight={700} fill="#333" clipPath={clip(b.id)}>{b.naam}</text>
          <text x={X(b.start) + 10} y={rijWp + 42} fontSize={14} fill="#444" clipPath={clip(b.id)}>{kortDatum(b.start)} - {kortDatum(b.eind)}</text>
        </g>
      ))}

      {/* sprints (agile werkpakketten) */}
      {sprints.map((s) => (
        <g key={s.id}>
          <rect x={X(s.start)} y={rijSprint} width={Math.max(6, X(addDagen(s.eind, 1)) - X(s.start) - 2)} height={26} fill={s.status === 'LOPEND' ? '#ffd9b3' : s.status === 'AFGESLOTEN' ? '#e4e9ed' : '#eef1f7'} stroke="#c9d1d8"><title>Sprint {s.nummer}: {kortDatum(s.start)} – {kortDatum(s.eind)}</title></rect>
          <text x={X(s.start) + 6} y={rijSprint + 18} fontSize={13} fill="#444">Sprint {s.nummer}</text>
        </g>
      ))}

      {/* vandaag-lijn door het kader */}
      {vd >= van && vd <= tot && <line x1={X(vd)} x2={X(vd)} y1={y0 - 26} y2={kaderEind} stroke="#3d7a35" strokeWidth={3} />}

      {/* mijlpalen: eerst alle lijnen en ruitjes, dan de labels (met een witte rand) erover */}
      {geplaatst.map((m) => (
        <g key={`l-${m.id}`}>
          <line x1={m.x} x2={m.x} y1={kaderEind} y2={kaderEind + 40 + m.n * 72 - 18} stroke="#999" strokeWidth={2} />
          <path d={`M${m.x} ${kaderEind - 9}l9 9l-9 9l-9 -9z`} fill={m.nadruk ? 'var(--merk)' : '#444'} />
        </g>
      ))}
      {geplaatst.map((m) => {
        const ly = kaderEind + 40 + m.n * 72;
        return (
          <g key={m.id}>
            <text x={m.x} y={ly} textAnchor="middle" fontSize={18} fill={ink} fontWeight={m.nadruk ? 700 : 400} stroke="var(--paneel)" strokeWidth={7} paintOrder="stroke">{m.naam}</text>
            <text x={m.x} y={ly + 24} textAnchor="middle" fontSize={15} fill={mute} stroke="var(--paneel)" strokeWidth={7} paintOrder="stroke">{kortDatum(m.datum)}</text>
          </g>
        );
      })}
    </svg>
  );
}
